import React, { useState, useEffect, useRef } from 'react';
import logo from '../logo.png';
import api from '../services/api';

function Clock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setT(new Date()), 1000); return () => clearInterval(iv); }, []);
  return (
    <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text2)', letterSpacing: 1, fontWeight: 600 }}>
      {t.toLocaleTimeString('fr-CA', { hour12: false })}
    </span>
  );
}

// « Mes rappels » (Karim 2026-10-21 : « open via the crm agents ») — the agent's
// private callbacks page (/callbacks/<work_token>) opens in a new tab from the
// portal. Link fetched once on mount from the authenticated endpoint.
function useWorkLink() {
  const [workLink, setWorkLink] = useState(null);
  useEffect(() => {
    let dead = false;
    api.get('/agents/work-link')
      // NOTE: the api client's response interceptor unwraps to r.data — d IS the
      // payload ({url_path}); reading d.data.url_path here renders nothing.
      .then(d => { const p = d?.url_path || d?.data?.url_path; if (!dead && p) setWorkLink(p); })
      .catch(() => { /* the button simply stays hidden */ });
    return () => { dead = true; };
  }, []);
  return workLink;
}

function Timer({ start, running, color, size }) {
  const [display, setDisplay] = useState('00:00:00');
  useEffect(() => {
    const tick = () => {
      if (!running && !start) { setDisplay('00:00:00'); return; }
      const elapsed = Math.floor((Date.now() - (start ? new Date(start).getTime() : Date.now())) / 1000);
      const h = Math.floor(elapsed / 3600), m = Math.floor((elapsed % 3600) / 60), s = elapsed % 60;
      setDisplay(`${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}`);
    };
    tick();
    if (!running && !start) return;
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [running, start]);
  return <span style={{ fontFamily: 'var(--mono)', fontSize: size || 14, fontWeight: 800, color: color || 'var(--text2)' }}>{display}</span>;
}

// Wrap-up countdown (Karim 2026-10-22): the backend reaper frees the agent at
// REAP_WRAPUP_SECONDS (120) after the customer hangs up — agents never knew
// the deadline existed. Shows the time LEFT; red under 30s. At zero the fiche
// moves to « Fiche précédente » instead of disappearing.
function WrapupCountdown({ endsAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  const left = Math.max(0, Math.round((new Date(endsAt).getTime() - now) / 1000));
  const m = Math.floor(left / 60), s = left % 60;
  const late = left <= 30;
  return (
    <span
      title="Temps restant avant que le wrap-up expire — la fiche reste récupérable via « Fiche précédente »"
      style={{
        fontFamily:'var(--mono)', fontSize:13, fontWeight:800, flexShrink:0,
        padding:'5px 12px', borderRadius:100,
        background: late ? 'rgba(239,68,68,0.12)' : 'rgba(139,92,246,0.12)',
        border: `2px solid ${late ? 'rgba(239,68,68,0.45)' : 'rgba(139,92,246,0.35)'}`,
        color: late ? 'var(--red)' : 'var(--purple)',
      }}>
      ⏱ {left === 0 ? 'EXPIRÉ' : `Wrap-up ${m}:${String(s).padStart(2,'0')}`}
    </span>
  );
}

export default function TopBar({ user, agentStatus, selectedCampaign, pauseCodes, callState, pauseStart, breakNote, fullUI, onStartCalls, onToggleOnline, onAudioCheck, onResume, onHangupAndDispo, onStatusChange, onLogout, wrapupEndsAt = null, prevFiche = null, onOpenPrevFiche = null, onOpenHistory = null, assignedCampaigns = [] }) {
  const workLink = useWorkLink();
  const [showPause, setShowPause] = useState(false);
  const [showBreakMenu, setShowBreakMenu] = useState(false);
  const [showCampaignMenu, setShowCampaignMenu] = useState(false);
  const [breakTip, setBreakTip] = useState(null);   // cloud tooltip next to Break
  const [startTip, setStartTip] = useState(false);  // cloud tooltip under Start Calls
  const [customMode, setCustomMode] = useState(false); // agent is typing a "Custom" reason
  const [customText, setCustomText] = useState('');
  const flashBreakTip = (msg) => { setBreakTip(msg); setTimeout(() => setBreakTip(null), 1500); };
  // Break reasons: fixed ones (alphabetical) first, then "Custom" last.
  const activeCodes = (pauseCodes || []).filter(p => p.active);
  const customCode  = activeCodes.find(p => p.code === 'CUSTOM' || p.label === 'Custom');
  const normalCodes = activeCodes.filter(p => p !== customCode).sort((a, b) => a.label.localeCompare(b.label));
  const closeBreakMenu = () => { setShowBreakMenu(false); setCustomMode(false); setCustomText(''); };
  const submitCustom = () => {
    const note = customText.trim();
    if (!note || !customCode) return;
    closeBreakMenu();
    onStatusChange('paused', customCode.id, note);
  };
  const isOnCall = agentStatus === 'oncall';
  const isPaused = agentStatus === 'paused';
  const isOffline = agentStatus === 'offline';
  const isWrapup = agentStatus === 'wrapup';
  const isLive = agentStatus === 'available' || isOnCall;
  const hasCall  = callState && ['ringing','answered'].includes(callState.status);
  // The disposition button must stay active after the customer hangs up ('ended')
  // so the agent can actually dispose the call (otherwise it greys out on hangup).
  const canDispo = callState && ['ringing','answered','ended'].includes(callState.status);

  const statusColors = { available: 'var(--green)', oncall: 'var(--coral)', wrapup: 'var(--purple)', paused: 'var(--orange)', online: 'var(--blue)', offline: 'var(--text2)' };
  // French display labels for the status badge (the underlying agentStatus values
  // sent to the backend are unchanged).
  const statusLabels = { available: 'Prêt', oncall: 'En appel', wrapup: 'Wrap-up', paused: 'Pause', online: 'En ligne', offline: 'Hors ligne' };
  const statusBg     = { available: 'rgba(16,185,129,0.1)', oncall: 'rgba(255,95,109,0.1)', wrapup: 'rgba(139,92,246,0.1)', paused: 'rgba(245,158,11,0.1)', online: 'rgba(59,130,246,0.1)', offline: '#f3f4f6' };
  const statusBorder = { available: 'rgba(16,185,129,0.3)', oncall: 'rgba(255,95,109,0.3)', wrapup: 'rgba(139,92,246,0.3)', paused: 'rgba(245,158,11,0.3)', online: 'rgba(59,130,246,0.3)', offline: '#e5e7eb' };

  const handleCampaignSwitch = async (camp) => {
    if (camp.id === selectedCampaign?.id) {
      setShowCampaignMenu(false);
      return;
    }
    // Switch campaign: login to new campaign, update selectedCampaign
    await onCampaignSwitch(camp.id);
    setSelectedCampaign(camp);
    setShowCampaignMenu(false);
  };

  return (
    <header style={{
      minHeight: 56,
      background: '#ffffff',
      borderBottom: '2px solid var(--border)',
      // Wrap onto a second row on small screens (e.g. a MacBook) so the right-side
      // cluster (status, queue, username, Logout) is never clipped off-screen.
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '6px 14px',
      rowGap: 6, gap: 8, flexShrink: 0,
      boxShadow: '0 2px 12px rgba(124,58,237,0.08)'
    }}>
      {/* Logo */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginRight:6, flexShrink:0 }}>
        <img src={logo} alt="Automynx" style={{
          width:32, height:32, objectFit:'contain',
        }} />
        <div>
          <div style={{ fontWeight:900, fontSize:14, letterSpacing:'-0.5px', color:'var(--text)', lineHeight:1 }}>{user?.account_name || 'MynxDialer'}</div>
          <div style={{ fontSize:9, color:'var(--purple)', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' }}>Portail agent</div>
        </div>
      </div>

      <div style={{ width:1, height:28, background:'var(--border)', flexShrink:0 }} />

      {/* Online/Offline presence toggle — independent of live-dialer connection */}
      <button
        onClick={onToggleOnline}
        disabled={isOnCall || isWrapup}
        title="Activer ou désactiver votre présence en ligne"
        style={{
          display:'flex', alignItems:'center', gap:8, padding:'5px 12px 5px 5px', height:34,
          borderRadius:100, cursor: (isOnCall || isWrapup) ? 'default' : 'pointer',
          border: `2px solid ${isOffline ? '#e5e7eb' : 'rgba(59,130,246,0.3)'}`,
          background: isOffline ? '#f3f4f6' : 'rgba(59,130,246,0.1)',
          fontWeight:800, fontSize:11, textTransform:'uppercase', letterSpacing:'0.05em',
          color: isOffline ? 'var(--text2)' : 'var(--blue)', flexShrink:0,
          opacity: (isOnCall || isWrapup) ? 0.6 : 1, transition:'all 0.2s'
        }}
      >
        <span style={{ position:'relative', width:34, height:20, borderRadius:100, background: isOffline ? '#d1d5db' : 'var(--blue)', transition:'background 0.2s', flexShrink:0 }}>
          <span style={{ position:'absolute', top:2, left: isOffline ? 2 : 16, width:16, height:16, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.35)', transition:'left 0.2s' }} />
        </span>
        {isOffline ? 'Hors ligne' : 'En ligne'}
      </button>

      {/* Live auto-dial connection — purely controls whether the agent is in the dialer queue */}
      <div style={{ position:'relative', flexShrink:0 }}>
        <button
          className={isLive ? 'btn btn-orange' : 'btn btn-green'}
          style={{ height:34 }}
          disabled={isOnCall || isOffline || isWrapup}
          title={isWrapup ? "Terminez le wrap-up (disposition) avant de changer ce réglage"
                : isLive ? 'Se déconnecter des appels automatiques en direct' : 'Se connecter aux appels automatiques en direct'}
          onClick={() => {
            // Can't start calls while on a break — nudge the agent to end it first.
            if (isPaused) { setStartTip(true); setTimeout(() => setStartTip(false), 1500); return; }
            onStartCalls();
          }}
        >
          {isLive ? '⏸ Arrêter les appels' : '▶ Démarrer les appels'}
        </button>
        {startTip && (
          <div style={{ position:'absolute', top:'calc(100% + 8px)', left:'50%', transform:'translateX(-50%)', background:'#1f2937', color:'#fff', padding:'7px 11px', borderRadius:10, fontSize:11, fontWeight:600, whiteSpace:'nowrap', zIndex:300, boxShadow:'0 6px 20px rgba(0,0,0,0.28)' }}>
            Terminez d'abord votre pause ☕
          </div>
        )}
      </div>

      {/* Break — usable only when calls are stopped; on break it flips to "End Break" */}
      <div style={{ position:'relative', flexShrink:0 }}>
        <button
          className={isPaused ? 'btn btn-orange' : 'btn'}
          style={isPaused ? { height:34 } : { height:34, background:'linear-gradient(135deg,#8b5cf6,#7c3aed)', color:'#fff', border:'none', boxShadow:'0 4px 12px rgba(139,92,246,0.35)' }}
          title={isPaused ? "Terminer votre pause" : "Prendre une pause (arrêtez d'abord les appels)"}
          onClick={() => {
            if (isPaused) { setShowBreakMenu(false); onStatusChange('online', null); return; } // end break -> online
            if (isLive)   { flashBreakTip("Appuyez d'abord sur « Arrêter les appels »"); return; }
            if (isOffline){ flashBreakTip("Passez d'abord en ligne"); return; }
            setShowBreakMenu(v => !v); // online & calls stopped -> pick a reason
          }}
        >
          {isPaused ? '☕ Terminer la pause' : '☕ Pause'}
        </button>
        {breakTip && (
          <div style={{ position:'absolute', top:'calc(100% + 8px)', left:'50%', transform:'translateX(-50%)', background:'#1f2937', color:'#fff', padding:'7px 11px', borderRadius:10, fontSize:11, fontWeight:600, whiteSpace:'nowrap', zIndex:300, boxShadow:'0 6px 20px rgba(0,0,0,0.28)' }}>
            {breakTip}
          </div>
        )}
        {showBreakMenu && !isPaused && (
          <div style={{ position:'absolute', top:'110%', left:0, zIndex:200, background:'#fff', border:'2px solid var(--border)', borderRadius:14, minWidth:200, boxShadow:'0 12px 32px rgba(124,58,237,0.15)', overflow:'hidden' }}>
            <div style={{ padding:'8px 14px', fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text2)', borderBottom:'2px solid var(--border)', background:'var(--bg-elevated)' }}>
              Motif de la pause
            </div>
            {normalCodes.map(pc => (
              <button key={pc.id}
                onClick={() => { closeBreakMenu(); onStatusChange('paused', pc.id); }}
                style={{ display:'block', width:'100%', padding:'9px 14px', background:'none', border:'none', color:'var(--text)', cursor:'pointer', fontSize:12, fontWeight:600, textAlign:'left', borderBottom:'1px solid var(--border)' }}
                onMouseEnter={e => { e.target.style.background = 'var(--bg-hover)'; e.target.style.color = 'var(--purple)'; }}
                onMouseLeave={e => { e.target.style.background = 'none'; e.target.style.color = 'var(--text)'; }}>
                ☕ {pc.label}
              </button>
            ))}
            {customCode && !customMode && (
              <button
                onClick={() => setCustomMode(true)}
                style={{ display:'block', width:'100%', padding:'9px 14px', background:'none', border:'none', color:'var(--purple)', cursor:'pointer', fontSize:12, fontWeight:700, textAlign:'left', borderBottom:'1px solid var(--border)' }}
                onMouseEnter={e => { e.target.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.target.style.background = 'none'; }}>
                ✏️ Personnalisé…
              </button>
            )}
            {customCode && customMode && (
              <div style={{ padding:'10px 12px', borderBottom:'1px solid var(--border)' }}>
                <input autoFocus className="inp"
                  placeholder="Écrivez votre motif…"
                  value={customText}
                  onChange={e => setCustomText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitCustom(); }}
                  style={{ width:'100%', marginBottom:8, fontSize:12 }} />
                <button
                  disabled={!customText.trim()}
                  onClick={submitCustom}
                  style={{ width:'100%', padding:'8px', borderRadius:8, border:'none', cursor: customText.trim() ? 'pointer' : 'default', color:'#fff', fontSize:12, fontWeight:700, background: customText.trim() ? 'linear-gradient(135deg,#8b5cf6,#7c3aed)' : '#c4b5fd' }}>
                  ☕ Partir en pause
                </button>
              </div>
            )}
            <button onClick={closeBreakMenu} style={{ display:'block', width:'100%', padding:'8px 14px', background:'none', border:'none', color:'var(--text2)', cursor:'pointer', fontSize:11, fontWeight:600 }}>Annuler</button>
          </div>
        )}
      </div>

      {/* Show the typed reason while on a custom break */}
      {isPaused && breakNote && (
        <span title={breakNote} style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 10px', borderRadius:100, background:'rgba(139,92,246,0.12)', border:'1px solid rgba(139,92,246,0.35)', color:'var(--purple)', fontSize:11, fontWeight:700, flexShrink:0, maxWidth:170, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          ☕ {breakNote}
        </span>
      )}

      {/* Deux temps (Karim, 2026-10-19) : en conversation → « RACCROCHER »
          (termine l'appel côté TRUNK, l'agent reste en wrap-up avec le script
          pour compléter/réserver le RDV) ; appel terminé → le même bouton
          devient « DISPOSITION ». Fini le un-clic qui permettait de disposer
          sans remplir le script. onHangupAndDispo = handler contextuel. */}
      {hasCall ? (
        <button className="btn btn-red" style={{ height:34 }} onClick={onHangupAndDispo}
          title="Termine l'appel — vous restez en wrap-up avec le script pour compléter le RDV, puis cliquez DISPOSITION">
          ✕ RACCROCHER
        </button>
      ) : (
        <button
          style={{ height:34, background:'linear-gradient(135deg,#8b5cf6,#7c3aed)', color:'#fff', border:'none', boxShadow:'0 4px 12px rgba(139,92,246,0.35)' }}
          disabled={!canDispo} onClick={onHangupAndDispo}
          title="Enregistrer le résultat de l'appel">
          ✓ DISPOSITION
        </button>
      )}

      {/* Multi-campaign switcher — agent can be active in multiple campaigns simultaneously */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <button
          onClick={() => setShowCampaignMenu(!showCampaignMenu)}
          disabled={isOnCall || isWrapup}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 100,
            background: 'rgba(124,58,237,0.06)',
            border: '2px solid rgba(124,58,237,0.18)',
            borderRadius: 100, fontSize: 12, fontWeight: 700,
            color: 'var(--purple)', whiteSpace: 'nowrap',
            cursor: (isOnCall || isWrapup) ? 'default' : 'pointer',
            opacity: (isOnCall || isWrapup) ? 0.6 : 1,
            transition: 'all 0.2s'
          }}
          title="Changer de campagne"
        >
          <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text2)', fontWeight: 800 }}>Campagne</span>
          {selectedCampaign?.name || '—'}
          <span style={{ marginLeft: 4 }}>▼</span>
        </button>

        {/* Campaign dropdown menu */}
        {showCampaignMenu && (
          <div style={{
            position: 'absolute', top: '110%', left: 0, zIndex: 300,
            background: '#fff', border: '2px solid var(--border)', borderRadius: 12,
            minWidth: 240, boxShadow: '0 12px 32px rgba(124,58,237,0.15)', overflow: 'hidden'
          }}>
            <div style={{ padding: '8px 14px', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text2)', borderBottom: '2px solid var(--border)', background: 'var(--bg-elevated)' }}>
              Campagnes actives ({assignedCampaigns?.length || 0})
            </div>
            {assignedCampaigns?.map(camp => (
              <button
                key={camp.id}
                onClick={() => handleCampaignSwitch(camp)}
                disabled={camp.id === selectedCampaign?.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '10px 14px', background: 'none',
                  border: 'none', color: 'var(--text)', cursor: 'pointer',
                  fontSize: 12, fontWeight: camp.id === selectedCampaign?.id ? 800 : 600,
                  textAlign: 'left', borderBottom: '1px solid var(--border)'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontWeight: 700, fontSize: 12 }}>{camp.name}</span>
                  <div style={{ display: 'flex', gap: 8, fontSize: 9, color: 'var(--text2)' }}>
                    <span>📞 {camp.dial_method || '—'}</span>
                    <span>⚡ {camp.dial_ratio || '—'}x</span>
                    <span style={{ color: camp.status === 'active' ? 'var(--green)' : 'var(--red)' }}>
                      {camp.status === 'active' ? '● Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>
                {camp.id === selectedCampaign?.id && (
                  <span style={{ color: 'var(--purple)', fontSize: 10, fontWeight: 800 }}>✓ Actuelle</span>
                )}
              </button>
            ))}
            <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 10, color: 'var(--text2)', textAlign: center }}>
              La campagne active change le script, le ratio et le trunk
            </div>
          </div>
        )}
      </div>

      <div style={{ width:1, height:28, background:'var(--border)', flexShrink:0 }} />

      {/* Status badge — display only in the simplified view (the Break ☕ menu
          above is the single pause entry point); agent01 keeps the dropdown. */}
      <div style={{ position:'relative' }}>
        <div
          onClick={() => { if (fullUI && !isOnCall) setShowPause(!showPause); }}
          style={{
            display:'inline-flex', alignItems:'center', gap:7, padding:'5px 14px',
            borderRadius:100, cursor: fullUI && !isOnCall ? 'pointer' : 'default', fontWeight:800, fontSize:11,
            textTransform:'uppercase', letterSpacing:'0.05em',
            background: statusBg[agentStatus] || '#f3f4f6',
            color: statusColors[agentStatus] || 'var(--text2)',
            border: `2px solid ${statusBorder[agentStatus] || '#e5e7eb'}`,
            transition:'all 0.2s',
          }}
        >
          <span style={{ width:8, height:8, borderRadius:'50%', background:'currentColor', display:'inline-block', boxShadow: agentStatus === 'available' ? '0 0 0 3px rgba(16,185,129,0.2)' : agentStatus === 'oncall' ? '0 0 0 3px rgba(255,95,109,0.2)' : 'none' }} />
          {statusLabels[agentStatus] || agentStatus?.toUpperCase() || 'Hors ligne'}
        </div>

        {fullUI && showPause && (
          <div style={{ position:'absolute', top:'110%', left:0, zIndex:200, background:'#fff', border:'2px solid var(--border)', borderRadius:14, minWidth:200, boxShadow:'0 12px 32px rgba(124,58,237,0.15)', overflow:'hidden' }}>
            <div style={{ padding:'8px 14px', fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text2)', borderBottom:'2px solid var(--border)', background:'var(--bg-elevated)' }}>
              Changer le statut
            </div>
            <button onClick={() => { setShowPause(false); onStatusChange('available', null); }}
              style={{ display:'block', width:'100%', padding:'10px 14px', background:'none', border:'none', color:'var(--green)', cursor:'pointer', fontSize:12, fontWeight:700, textAlign:'left', borderBottom:'1px solid var(--border)' }}>
              ● PRÊT
            </button>
            {pauseCodes.filter(p => p.active).map(pc => (
              <button key={pc.id}
                onClick={() => { setShowPause(false); onStatusChange('paused', pc.id); }}
                style={{ display:'block', width:'100%', padding:'9px 14px', background:'none', border:'none', color:'var(--text)', cursor:'pointer', fontSize:12, fontWeight:600, textAlign:'left', borderBottom:'1px solid var(--border)' }}
                onMouseEnter={e => { e.target.style.background = 'var(--bg-hover)'; e.target.style.color = 'var(--purple)'; }}
                onMouseLeave={e => { e.target.style.background = 'none'; e.target.style.color = 'var(--text)'; }}>
                ⏸ {pc.label}
              </button>
            ))}
            <button onClick={() => setShowPause(false)} style={{ display:'block', width:'100%', padding:'8px 14px', background:'none', border:'none', color:'var(--text2)', cursor:'pointer', fontSize:11, fontWeight:600 }}>Annuler</button>
          </div>
        )}
      </div>

      {/* Wrap-up countdown — visible to EVERY agent (not just agent01): the
          120s cap was invisible until the fiche vanished (2026-10-22). */}
      {isWrapup && wrapupEndsAt && <WrapupCountdown endsAt={wrapupEndsAt} />}

      {/* « Fiche précédente » — the wrap-up cap reaped a fiche the agent was
          still filling. One click reopens it; the late disposition is accepted
          by the backend. Kept until dispositioned (survives next calls). */}
      {prevFiche && onOpenPrevFiche && (
        <button
          onClick={onOpenPrevFiche}
          title="Le wrap-up a expiré sur cette fiche — terminez et enregistrez sa disposition maintenant (acceptée même tardivement)"
          style={{
            display:'flex', alignItems:'center', gap:6, height:34, padding:'5px 12px', flexShrink:0,
            borderRadius:100, cursor:'pointer', fontWeight:800, fontSize:11,
            textTransform:'uppercase', letterSpacing:'0.04em',
            background:'rgba(239,68,68,0.12)', color:'var(--red)',
            border:'2px solid rgba(239,68,68,0.45)',
            animation:'pulse-r 1.2s ease-in-out infinite'
          }}>
          📋 Fiche précédente{prevFiche.lead ? ` — ${prevFiche.lead.first_name || ''} ${prevFiche.lead.last_name || ''}`.trim() : ''}
        </button>
      )}

      {fullUI && (
        <Timer
          start={isPaused ? pauseStart : isOnCall ? callState?.answered_at : isWrapup ? callState?.ended_at : null}
          running={isPaused || isOnCall || isWrapup}
          color={isPaused ? 'var(--orange)' : isOnCall ? 'var(--coral)' : isWrapup ? 'var(--purple)' : 'var(--text2)'}
        />
      )}

      <div style={{ width:1, height:28, background:'var(--border)', flexShrink:0 }} />

      {/* Timers */}
      {[
        { label:'PAUSE', start: pauseStart, running: isPaused, color: isPaused ? 'var(--orange)' : 'var(--text2)' },
        { label:'APPEL',  start: callState?.answered_at, running: isOnCall, color: isOnCall ? 'var(--coral)' : 'var(--text2)' },
        { label:'DISPO', start: callState?.status === 'ended' ? callState?.ended_at : null, running: callState?.status === 'ended', color: callState?.status === 'ended' ? 'var(--purple)' : 'var(--text2)' }
      ].map(t => (
        <div key={t.label} style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'0 10px', borderRight:'1px solid var(--border)' }}>
          <div style={{ fontSize:8, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--purple)', marginBottom:1 }}>{t.label}</div>
          <Timer start={t.start} running={t.running} color={t.color} size={12} />
        </div>
      ))}

      {/* Right */}
      <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10 }}>
        <Clock />
        {/* The "File" (queue) chip is gone: its counter only ever incremented on
            call:started and never reset — a cumulative dial count, not a queue
            depth, and nothing an agent can act on. The "APPEL EN DIRECT" badge
            duplicated the status badge + the webphone call box — kept for agent01. */}
        {fullUI && (
          <div style={{
            padding:'5px 12px', borderRadius:100, fontSize:11, fontWeight:800, textTransform:'uppercase',
            background: hasCall ? 'rgba(255,95,109,0.12)' : 'rgba(16,185,129,0.08)',
            color: hasCall ? 'var(--coral)' : 'var(--green)',
            border: `2px solid ${hasCall ? 'rgba(255,95,109,0.3)' : 'rgba(16,185,129,0.25)'}`,
          }}>
            {hasCall ? '● APPEL EN DIRECT' : 'Aucun appel'}
          </div>
        )}
        <span style={{ fontSize:12, color:'var(--text2)', fontWeight:700 }}>{user?.full_name || user?.username}</span>
        {onOpenHistory && (
          <button className="btn btn-ghost btn-sm" onClick={onOpenHistory}
                  title="Vos appels passés + recherche de leads (toutes campagnes)">
            📜 Historique
          </button>
        )}
        {workLink && (
          <a href={workLink} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"
             style={{ textDecoration:'none' }} title="File de rappels perso : no-show et rappels à replanifier">
            Mes rappels
          </a>
        )}
        <button className="btn btn-ghost btn-sm" onClick={onLogout}>Déconnexion</button>
      </div>
    </header>
  );
}